import { authenticatedFetch } from "./apiClient";

export interface OfferGenerationRequest {
  page_url: string;
  domain_id: string;
  page_id: string;
}

export interface CustomOfferGenerationRequest {
  page_url: string;
  domain_id: string;
  page_id: string;
  offer_description: string;
}

export interface OfferGenerationResponse {
  success: boolean;
  message: string;
  offer_id: string;
}

export interface OfferEnabledResponse {
  offer_id: string;
  is_enabled: boolean;
}

export class OfferGenerationService {
  /**
   * Toggle whether an offer feeds generation.
   *
   * Backend-mediated: offers are `allow write: if false` in firestore.rules,
   * so the client cannot write `is_enabled` directly.
   */
  static async setOfferEnabled(
    offerId: string,
    domainId: string,
    isEnabled: boolean,
  ): Promise<OfferEnabledResponse> {
    const response = await authenticatedFetch(
      `${import.meta.env.VITE_API_URL}/v1/generations/offers/${offerId}/enabled`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ domain_id: domainId, is_enabled: isEnabled }),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to update offer: ${response.statusText}`);
    }

    return response.json();
  }

  static async generateOffer(
    request: OfferGenerationRequest,
  ): Promise<OfferGenerationResponse> {
    const response = await authenticatedFetch(
      `${import.meta.env.VITE_API_URL}/v1/generations/offers/single`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      throw new Error(`Generation failed: ${response.statusText}`);
    }

    return response.json();
  }

  static async generateCustomOffer(
    request: CustomOfferGenerationRequest,
  ): Promise<OfferGenerationResponse> {
    const response = await authenticatedFetch(
      `${import.meta.env.VITE_API_URL}/v1/generations/offers/custom`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      throw new Error(`Generation failed: ${response.statusText}`);
    }

    return response.json();
  }
}
